import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import { useLocation, useRouteMatch } from 'react-router';
import qs from 'query-string';

import {
    useActivateDistributorBillsMutation,
    useExportDistributorCompaniesMutation,
    useGetDistributorBillsQuery,
    useGetDistributorCompaniesQuery,
    useTestDistributorBillsMutation,
} from 'api/main';
import { distributorSchemas } from 'schemas';
import { userSelectors } from 'reducers';
import {
    ORDER_BY,
    RoleNames,
    billStatus,
    sortDirection,
    queryStringifyOptions,
    listingActionButtonStyle,
} from 'utils/constants';
import { useDebounce, useLocalStorage, useMatchAtLeastOneRole } from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import DistributorRow from './DistributorRow';
import {
    ConfirmDialog,
    CustomTablePagination,
    ActionsMenu,
    EmptyTableBody,
    EnhancedTableHead,
    Loader,
    QuickSearchField,
    ActionButton,
} from 'components';
import { MenuActivate, MenuExport, MenuTest } from 'components/ActionsMenu/actions';
import {
    TableRow,
    Table,
    TableBody,
    TableContainer,
    TableFooter,
    Typography,
    Box,
} from '@mui/material';

const tableHeads = [
    { id: ORDER_BY.distributorCompanyStatus, isSortable: true, width: 7, label: texts.STATUT },
    { id: ORDER_BY.distributorCompanyName, isSortable: true, width: 25, label: texts.COMPANY_NAME },
    { id: ORDER_BY.createdAt, isSortable: true, width: 25, label: texts.DATE },
    {
        id: 'pendingSubscriptions',
        isSortable: false,
        width: 20,
        label: texts.ONGOING_SUBSCRIPTIONS,
    },
    { id: 'investorsNumber', isSortable: false, width: 15, label: texts.FINISHED_SUBSCRIPTIONS },
    { id: 'actions', isSortable: false, width: 8, label: '' },
];

const ActivateStatementAction = React.forwardRef((props, ref) => (
    <MenuActivate ref={ref} {...props} />
));
const TestStatementAction = React.forwardRef((props, ref) => <MenuTest ref={ref} {...props} />);
const FileExportAction = React.forwardRef((props, ref) => <MenuExport ref={ref} {...props} />);

const DistributorsTable = () => {
    const { path } = useRouteMatch();
    const location = useLocation();
    const dispatch = useDispatch();
    const userIsNetworkAdmin = useMatchAtLeastOneRole([RoleNames.NETWORK_ADMIN]);
    const userIsAdminOrSignatoryServicer = useMatchAtLeastOneRole([
        RoleNames.SERVICER_ADMIN,
        RoleNames.SERVICER_SIGNATORY,
    ]);

    const { data: { 0: distributorBill } = { 0: {} }, isFetching: dataLoading } =
        useGetDistributorBillsQuery(null, {
            skip: !userIsAdminOrSignatoryServicer,
        });
    const [exportDistributorCompanies, { isLoading: exportLoading }] =
        useExportDistributorCompaniesMutation();
    const [testDistributorBills, { isLoading: testLoading }] = useTestDistributorBillsMutation();
    const [activateDistributorBills, { isLoading: activateLoading, isSuccess: activateSuccess }] =
        useActivateDistributorBillsMutation();

    const internalUser = useSelector(userSelectors.getInternalUser);

    const hasOneTimeFilters = useMemo(() => !!location.search, [location]);
    const statementCompleted = useMemo(() => {
        const payload = distributorSchemas.getDistributorBills();
        return (
            distributorBill?.quarter === payload.quarter &&
            distributorBill?.year === payload.year &&
            (distributorBill?.status === billStatus.COMPLETED ||
                distributorBill?.status === billStatus.ACTIVATED)
        );
    }, [distributorBill]);

    const {
        distributor_activity_0_3 = false,
        distributor_activity_3_7 = false,
        distributor_activity_7_30 = false,
        distributor_activity_30_plus = false,
        conventionPartiallySigned = false,
        requestForDistribution = false,
        distributorSubscriptionInProgress = false,
        noConventionFullySigned = false,
        oneOrMoreConventionsFullySigned = false,
        oneOrMoreSubscriptionsFinished = false,
    } = useMemo(() => qs.parse(location.search, queryStringifyOptions), [location]);

    const [activateDialogOpen, setActivateDialogOpen] = useState(false);
    const [keyword, setKeyword] = useState('');
    const debouncedKeyword = useDebounce(keyword, 500);

    const activeImpersonation = useMemo(() => !!internalUser?.impersonatedByUser, [internalUser]);

    const defaultFilters = useMemo(
        () => ({
            orderBy: ORDER_BY.createdAt,
            direction: sortDirection.DESC,
            itemsPerPage: 15,
            distributor_activity_0_3: distributor_activity_0_3 ? 1 : undefined,
            distributor_activity_3_7: distributor_activity_3_7 ? 1 : undefined,
            distributor_activity_7_30: distributor_activity_7_30 ? 1 : undefined,
            distributor_activity_30_plus: distributor_activity_30_plus ? 1 : undefined,
            conventionPartiallySigned: conventionPartiallySigned ? 1 : undefined,
            requestForDistribution: requestForDistribution ? 1 : undefined,
            distributorSubscriptionInProgress: distributorSubscriptionInProgress ? 1 : undefined,
            noConventionFullySigned: noConventionFullySigned ? 1 : undefined,
            oneOrMoreConventionsFullySigned: oneOrMoreConventionsFullySigned ? 1 : undefined,
            oneOrMoreSubscriptionsFinished: oneOrMoreSubscriptionsFinished ? 1 : undefined,
            status: hasOneTimeFilters
                ? []
                : ['pending_validation', 'pending_signature', 'completed'],
            page: 1,
        }),
        [
            distributor_activity_0_3,
            distributor_activity_3_7,
            distributor_activity_7_30,
            distributor_activity_30_plus,
            conventionPartiallySigned,
            requestForDistribution,
            distributorSubscriptionInProgress,
            hasOneTimeFilters,
            noConventionFullySigned,
            oneOrMoreConventionsFullySigned,
            oneOrMoreSubscriptionsFinished,
        ]
    );

    const [filters, setFilters] = useLocalStorage(
        'distributorFilters',
        defaultFilters,
        activeImpersonation || hasOneTimeFilters || !!debouncedKeyword
    );

    const {
        data: { distributors = [], lastPage } = { distributors: [], lastPage: 1 },
        isFetching: listLoader,
    } = useGetDistributorCompaniesQuery(distributorSchemas.getDistributorList(filters));

    const handlePageChange = useCallback(
        page => () => setFilters(oldValue => ({ ...oldValue, page })),
        [setFilters]
    );

    const handleSortDistributors = useCallback(
        (property, direction) => {
            setFilters(oldValue => ({ ...oldValue, orderBy: property, direction }));
        },
        [setFilters]
    );

    const handleExportList = useCallback(
        payload => () => {
            exportDistributorCompanies(payload);
        },
        [exportDistributorCompanies]
    );

    const handleCreateDistributor = useCallback(
        () => dispatch(push(`${path}/create`)),
        [dispatch, path]
    );

    useEffect(() => {
        if (activateSuccess) {
            setActivateDialogOpen(false);
        }
    }, [activateSuccess]);

    useEffect(() => {
        setFilters(prevState => ({
            ...prevState,
            page: 1,
            keyword: debouncedKeyword,
            direction:
                !!debouncedKeyword || hasOneTimeFilters ? prevState.direction : sortDirection.DESC,
            orderBy:
                !!debouncedKeyword || hasOneTimeFilters
                    ? prevState.orderBy
                    : ORDER_BY.subscriptionId,
        }));
    }, [debouncedKeyword, setFilters, hasOneTimeFilters]);

    useEffect(() => {
        // if all one time filters are removed from URL we should return to the previous filters(localStorage or default)
        if (!hasOneTimeFilters) {
            setFilters(prevState => ({
                ...prevState,
                distributor_activity_0_3: undefined,
                distributor_activity_3_7: undefined,
                distributor_activity_7_30: undefined,
                distributor_activity_30_plus: undefined,
                conventionPartiallySigned: undefined,
                requestForDistribution: undefined,
            }));
        }
    }, [setFilters, hasOneTimeFilters]);

    useEffect(() => {
        if (filters.page > lastPage && lastPage > 0) {
            setFilters(prevState => ({ ...prevState, page: lastPage }));
        }
    }, [lastPage, filters, setFilters]);

    return (
        <React.Fragment>
            <ConfirmDialog
                open={activateDialogOpen}
                confirmText={texts.CONFIRMER}
                cancelText={texts.CANCEL}
                title={texts.QUARTER_STATEMENT_ACTIVATION}
                loading={activateLoading}
                handleClose={() => setActivateDialogOpen(false)}
                handleConfirm={activateDistributorBills}
            >
                {texts.CONFIRM_QUARTER_STATEMENT_ACTIVATION}
            </ConfirmDialog>
            <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="h5" align="left">
                        {texts.DISTRIBUTEUR_LIST_TITLE}
                    </Typography>
                    {userIsAdminOrSignatoryServicer && (
                        <ActionButton
                            variant="outlined"
                            sx={listingActionButtonStyle}
                            onClick={handleCreateDistributor}
                        >
                            {texts.DISTRIBUTOR_FAST_CREATE}
                        </ActionButton>
                    )}
                </Box>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <QuickSearchField
                        value={keyword}
                        onChange={e => setKeyword(e.target.value)}
                        handleClear={() => setKeyword('')}
                    />
                    {!userIsNetworkAdmin && (
                        <Box display="flex" alignItems="flex-end" flexDirection="column">
                            <ActionsMenu
                                autoClose
                                buttonStyle="text"
                                buttonProps={{ variant: 'outlined', sx: listingActionButtonStyle }}
                                buttonText={texts.EXPORTER_LISTE}
                                containerProps={{ mb: 1 }}
                            >
                                <FileExportAction
                                    label={texts.EXPORT_DISTRIBUTORS}
                                    disabled={exportLoading}
                                    onClick={handleExportList({ detailed: true })}
                                />
                                <FileExportAction
                                    label={texts.EXPORT_USERS}
                                    disabled={exportLoading}
                                    onClick={handleExportList()}
                                />
                            </ActionsMenu>
                            {userIsAdminOrSignatoryServicer && (
                                <Loader loading={activateLoading || testLoading || dataLoading}>
                                    <ActionsMenu
                                        autoClose
                                        buttonStyle="text"
                                        buttonProps={{
                                            variant: 'outlined',
                                            sx: listingActionButtonStyle,
                                        }}
                                        buttonText={texts.QUARTER_STATEMENT}
                                    >
                                        <ActivateStatementAction
                                            disabled={statementCompleted}
                                            onClick={() => setActivateDialogOpen(true)}
                                        />
                                        <TestStatementAction
                                            disabled={statementCompleted}
                                            onClick={testDistributorBills}
                                        />
                                    </ActionsMenu>
                                </Loader>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
            {!listLoader && debouncedKeyword && (
                <Typography variant="body2" gutterBottom>
                    {texts.RESULTS_FOR_KEYWORD(debouncedKeyword)}
                </Typography>
            )}
            <TableContainer>
                <Table style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                        {tableHeads.map(c => (
                            <col key={c.id} width={`${c.width}%`} />
                        ))}
                    </colgroup>
                    <EnhancedTableHead
                        headCells={tableHeads}
                        orderBy={filters.orderBy}
                        direction={filters.direction}
                        handleSort={handleSortDistributors}
                    />
                    {listLoader || distributors.length === 0 ? (
                        <EmptyTableBody colSpan={tableHeads.length} loading={listLoader} />
                    ) : (
                        <TableBody>
                            {distributors.map(company => (
                                <DistributorRow key={company[0].id} company={company} />
                            ))}
                        </TableBody>
                    )}
                    <TableFooter>
                        <TableRow>
                            <CustomTablePagination
                                currentPage={filters.page}
                                lastPage={lastPage}
                                changePage={handlePageChange}
                                columns={tableHeads.length}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </React.Fragment>
    );
};

export default DistributorsTable;
